import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ComboBox } from "~/components/common/combo-box";
import { ContentContainer } from "~/components/common/content-container";
import { Dialog } from "~/components/common/Dialog";
import { Button } from "~/components/ui/button";
import type { MqttBroker, MqttBrokerDto } from "~/types/api";
import type {
  BulkFormData,
  FormOption,
  FormStructure,
  UseStructureResult,
} from "~/types/ui";
import { SoForm } from "../form/so-form";
import { Loading } from "~/components/common/loading";
import { MqttApi } from "~/api/mqtt-api";
import { toast } from "sonner";
import z from "zod";
import { FormFieldType } from "../form/fields";
import { MqttSSLModeEnum, MqttVersionEnum } from "~/utils/mqtt-util";

export default function BrokerSelecter({
  value,
  onChange,
  options,
  canCreate = false,
}: {
  value: string;
  onChange: (val: string) => void;
  options: MqttBroker[];
  canCreate?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex w-auto">
      <div className="w-full">
        <ComboBox
          options={options?.map(
            (d) =>
              ({
                label: d.displayName,
                value: d.mqttBrokerId,
              }) as FormOption<string>,
          )}
          value={value}
          onChange={(val) => onChange(val as string)}
          placeholder={t("mqtt.brokers.placeholders.brokerSelect")}
        />
      </div>
      {canCreate && <BrokerCreate />}
    </div>
  );
}

export function BrokerCreate() {
  const { t } = useTranslation();
  const [brokerDialog, setBrokerDialog] = useState<boolean>(false);

  return (
    <Dialog
      title={t("mqtt.brokers.CreateTitle")}
      description={t("mqtt.brokers.CreateDescription")}
      width="w-full"
      maxWidth="w-full"
      open={brokerDialog}
      onOpenChange={setBrokerDialog}
      trigger={
        <Button variant="ghost" aria-label="Edit Selected">
          <Plus className="h-[1.2rem] w-[1.2rem] text-green-600 " />
        </Button>
      }
      children={
        <ContentContainer>
          <BrokerCreateForm onCreate={() => setBrokerDialog(false)} />
        </ContentContainer>
      }
    />
  );
}

export function BrokerCreateForm({
  data = {},
  onCreate,
}: {
  data?: any;
  onCreate: (broker: any) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>();

  const structure = useBrokerStructure(data);
  if (structure.isLoading || Object.keys(structure.structure).length === 0)
    return <Loading />;

  async function handleSubmit(
    data: MqttBrokerDto | BulkFormData<MqttBrokerDto>,
  ) {
    const mqttApi = new MqttApi();

    setIsLoading(true);
    const testResult = await mqttApi.testBroker(data as MqttBrokerDto);
    if (!testResult.ok || !testResult.data?.wasSuccessful) {
      toast.error(t("mqtt.errors.brokerTestFail"));
      console.error(
        "Failed testing of Broker configurations",
        testResult.ok ? t(testResult.data?.message ?? "") : testResult.error,
      );
      setIsLoading(false);
      return;
    }

    const result = await mqttApi.createBroker(data as MqttBrokerDto);
    if (!result.ok) {
      toast.error(t("common.errors.general"));
      console.error("Failed to create new Broker", result.error);
      setIsLoading(false);
      return;
    }

    toast.success(t("common.submitted"));
    queryClient.invalidateQueries({ queryKey: ["brokers:all"] });
    setIsLoading(false);
    onCreate(data as MqttBrokerDto);
  }
  if (isLoading)
    return (
      <>
        {t("mqtt.brokers.testing")}
        <Loading />
      </>
    );
  return <SoForm structure={structure.structure} onSubmit={handleSubmit} />;
}

function useBrokerStructure(
  data: MqttBrokerDto,
): UseStructureResult<MqttBrokerDto> {
  const { t } = useTranslation();
  const isLoading = false;
  const structure: FormStructure<MqttBrokerDto> = {
    hostname: {
      type: FormFieldType.TEXT,
      label: "mqtt.brokers.fields.hostname",
      placeholder: "mqtt.brokers.placeholders.hostname",
      default: data?.hostname || "",
      rules: z.string().min(2, "mqtt.brokers.validation.hostnameRequired"),
    },

    port: {
      type: FormFieldType.NUMBER,
      label: "mqtt.brokers.fields.port",
      placeholder: "mqtt.brokers.placeholders.port",
      default: data?.port || "1883",
      rules: z.string().min(2, "mqtt.brokers.validation.portRequired"),
    },

    version: {
      type: FormFieldType.COMBOBOX,
      label: "mqtt.brokers.fields.version",
      placeholder: "mqtt.brokers.placeholders.version",
      default: data?.version || 0,
      options:
        (Object.values(MqttVersionEnum).map((x) => {
          return {
            label: t("mqtt.brokers.versions." + x),
            value: x,
          } as FormOption<number>;
        }) as FormOption<number>[]) ?? [],
    },

    sslMode: {
      type: FormFieldType.COMBOBOX,
      label: "mqtt.brokers.fields.sslMode",
      placeholder: "mqtt.brokers.placeholders.sslMode",
      default: data?.sslMode || 0,
      options:
        (Object.values(MqttSSLModeEnum).map((x) => {
          return {
            label: t("mqtt.brokers.sllModes." + x),
            value: x,
          } as FormOption<number>;
        }) as FormOption<number>[]) ?? [],
      rerender: true,
    },

    caCertificate: {
      type: FormFieldType.TEXT,
      label: "mqtt.brokers.fields.caCertificate",
      placeholder: "mqtt.brokers.placeholders.caCertificate",
      default: data?.caCertificate || "",
      hiddenFn: (form) => {
        const sslMode = form.getValues("sslMode") as number;

        return !(sslMode == MqttSSLModeEnum.SELF_SIGNED);
      },
    },

    useAuth: {
      type: FormFieldType.CHECKBOX,
      label: "mqtt.brokers.fields.useAuth",
      placeholder: "mqtt.brokers.placeholders.useAuth",
      default: data?.useAuth || false,
      rerender: true,
    },

    username: {
      type: FormFieldType.TEXT,
      label: "mqtt.brokers.fields.username",
      placeholder: "mqtt.brokers.placeholders.username",
      default: data?.username || "",
      hiddenFn: (form) => {
        return !(form.getValues("useAuth") as boolean);
      },
    },

    password: {
      type: FormFieldType.PASSWORD,
      label: "mqtt.brokers.fields.password",
      placeholder: "mqtt.brokers.placeholders.password",
      default: data?.password || "",
      hiddenFn: (form) => {
        return !(form.getValues("useAuth") as boolean);
      },
    },

    isGlobal: {
      type: FormFieldType.CHECKBOX,
      label: "mqtt.brokers.fields.isGlobal",
      placeholder: "mqtt.brokers.placeholders.isGlobal",
      default: data?.isGlobal || false,
    },
  };

  return { structure, isLoading };
}
